import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Plus } from "lucide-react";
import { useLang } from "../lang";
import { PRODUCTS, isRender } from "../catalog";
import { familyOf, FAMILY_LABELS, MAX_COMPARE } from "../compare";
import { useCompare } from "./CompareContext";

export default function ComparePicker({ open, onClose }) {
  const { lang } = useLang();
  const { slugs, toggle } = useCompare();
  const fam = slugs[0] ? familyOf(PRODUCTS[slugs[0]]) : null;
  const options = fam ? Object.values(PRODUCTS).filter((p) => familyOf(p) === fam) : [];
  const full = slugs.length >= MAX_COMPARE;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && fam && (
        <motion.div className="fixed inset-0 z-[80] flex items-end md:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="compare-picker">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.3, ease: [0.19, 1, 0.22, 1] }}
            className="relative w-full md:max-w-[560px] max-h-[80vh] bg-white rounded-t-3xl md:rounded-3xl flex flex-col overflow-hidden"
          >
            <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-[#e4e4e7]">
              <div>
                <div className="text-[18px] font-semibold text-[#1d1d1f]">{lang === "it" ? "Aggiungi al confronto" : "Add to comparison"}</div>
                <div className="text-[13px] text-[#6e6e73] mt-0.5">
                  {FAMILY_LABELS[fam][lang]} · {slugs.length}/{MAX_COMPARE} {lang === "it" ? "selezionati" : "selected"}
                </div>
              </div>
              <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-[#f5f5f7] hover:bg-[#e4e4e7] flex items-center justify-center shrink-0" aria-label="Chiudi" data-testid="compare-picker-close">
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto px-3 py-2" data-lenis-prevent>
              {options.map((p) => {
                const active = slugs.includes(p.slug);
                const disabled = !active && full;
                return (
                  <button
                    key={p.slug}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggle(p.slug, lang)}
                    className={`w-full flex items-center gap-4 px-3 py-3 rounded-xl text-left transition-colors duration-200 ${active ? "bg-[#1D3F94]/[0.06]" : "hover:bg-[#f5f5f7]"} ${disabled ? "opacity-40" : ""}`}
                    data-testid={`compare-picker-item-${p.slug}`}
                  >
                    <div className="w-14 h-11 rounded-lg bg-white border border-[#e4e4e7] overflow-hidden shrink-0">
                      <img src={p.image} alt="" loading="lazy" className={`w-full h-full ${isRender(p.image) ? "object-contain p-1" : "object-cover"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px] font-semibold text-[#1d1d1f] truncate">{p[lang].name}</div>
                      <div className="text-[12.5px] text-[#6e6e73] truncate">{p[lang].tagline}</div>
                    </div>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-[#1D3F94] text-white" : "border border-[#c7c7cc] text-[#1D3F94]"}`}>
                      {active ? <Check size={14} strokeWidth={3} /> : <Plus size={14} />}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-[#e4e4e7] pb-[max(1rem,env(safe-area-inset-bottom))]">
              {full && <p className="mb-3 text-center text-[12.5px] text-[#6e6e73]" data-testid="compare-picker-max">{lang === "it" ? `Puoi confrontare al massimo ${MAX_COMPARE} sistemi: rimuovine uno per aggiungerne un altro.` : `You can compare up to ${MAX_COMPARE} systems: remove one to add another.`}</p>}
              <button type="button" onClick={onClose} className="btn-pill btn-primary w-full justify-center" data-testid="compare-picker-done">
                {lang === "it" ? "Fatto" : "Done"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
